import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { FaGlobe, FaArrowLeft, FaTimes } from "react-icons/fa";
import { useLanguage } from "../../components/LanguageContext";
import { ClickToPath, Language, CreateStepData } from "../../Types/types";
import { addpackagetranslation } from "./addpackagetranslation";
import { AddLanguageModal } from "../../components/shared/AddLanguageModal";
import PackageListHeader from "../../components/Headers/PackageListHeader";
import RotationInput from "./RotationInput";
import PanoramaInputs from "./PanoramaInputs";
import { useDispatch, useSelector } from "react-redux";
import {
  AppDispatch,
  RootState,
} from "../../components/Manual_List/fetch/store";
import {
  fetchContentTypes,
  fetchFullPackageById,
  createStep,
  fetchLinksForPanoramaCreate,
  postLink,
  uploadContentThumbnail,
  uploadContent,
} from "../../components/Manual_List/fetch/slices";
import { secondaryFiletrnslts } from "./secondaryFiletrnslts";

const CreateStep: React.FC = () => {
  const { language } = useLanguage();
  const t = addpackagetranslation[language as Language];
  const t2 = secondaryFiletrnslts[language as Language];
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const [stepNumbers, setStepNumbers] = useState<number[]>([]);
  const state = location.state || {};
  const contentTypes = useSelector(
    (state: RootState) => state.data.contentTypes.data
  );
  const { id } = useParams<{ id: string }>();
  const [stepNames, setStepNames] = useState<Record<string, string>>({});
  const [stepType, setStepType] = useState<string>("");
  const [stepFile, setStepFile] = useState<File | null>(null);
  const [secondaryFile, setSecondaryFile] = useState<File | null>(null);
  const [stepRotation, setStepRotation] = useState<number>(0);
  const [stepClickToPaths, setStepClickToPaths] = useState<ClickToPath[]>([]);
  const [isStepNameModalOpen, setIsStepNameModalOpen] = useState(false);
  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);

  const links = useSelector((state: RootState) => state.data.link.data);

  useEffect(() => {
    dispatch(fetchContentTypes(language));
    if (id) {
      dispatch(fetchFullPackageById({ id, language }));
      dispatch(fetchLinksForPanoramaCreate({ id }));
    }
  }, [dispatch, language, id]);

  useEffect(() => {
    if (location.state && location.state.steps) {
      setStepNumbers(location.state.steps);
    }
  }, [location.state]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const selectedContentType = contentTypes.find(
      (type) => type.name === stepType
    );
    if (!selectedContentType) {
      console.error("Selected content type is invalid.");
      return;
    }

    let stepSettings: any = {};
    if (stepType === "object") {
      stepSettings.rotation = stepRotation;
    }

    const nextStepNumber =
      stepNumbers.length > 0 ? Math.max(...stepNumbers) + 1 : 1;

    const stepData: CreateStepData = {
      packageId: parseInt(id as string),
      label: stepNames,
      step: nextStepNumber,
      typeId: selectedContentType.id,
      settings: JSON.stringify(stepSettings),
    };

    const result = await dispatch(createStep(stepData));

    if (result.meta.requestStatus === "fulfilled") {
      const createdStepId = result.payload[0]?.id;

      if (thumbnail && createdStepId) {
        await dispatch(
          uploadContentThumbnail({ id: createdStepId, thumbnail })
        );
      }

      stepClickToPaths.forEach((clickToPath) => {
        const linkData = {
          packageContentId: createdStepId,
          targetPackageContentId: clickToPath.targetPackageContentId ?? 0,
          settings: clickToPath.angle.toString(),
        };

        dispatch(postLink(linkData)).then((linkResult) => {
          if (linkResult.meta.requestStatus === "fulfilled") {
          }
        });
      });

      if (stepFile && createdStepId) {
        await dispatch(
          uploadContent({
            id: createdStepId,
            primaryFile: stepFile,
            secondaryFile: secondaryFile || undefined,
          })
        );
      }

      navigate(`/steps-list/${id}`);
    }
  };

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setFile: (file: File | null) => void
  ) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleRemoveFile = (setFileFunction: (file: File | null) => void) => {
    setFileFunction(null);
  };

  const handleRemoveThumbnail = () => {
    setThumbnail(null);
    setThumbnailUrl(null);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (
      e.type === "dragenter" ||
      e.type === "dragleave" ||
      e.type === "dragover"
    ) {
      setDragActive(e.type === "dragenter" || e.type === "dragover");
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setThumbnail(e.dataTransfer.files[0]);
      setThumbnailUrl(URL.createObjectURL(e.dataTransfer.files[0]));
    }
  };

  const capitalizeFirstLetter = (string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  };

  return (
    <div className="min-h-screen w-screen flex flex-col bg-gray-100 dark:bg-darkBackground dark:text-white">
      <PackageListHeader />
      <div className="flex flex-col items-center justify-start flex-grow">
        <form
          id="add-steps-form"
          className="w-full px-4 sm:px-6 md:px-8 py-6 max-w-7xl flex flex-col relative"
          onSubmit={handleSubmit}
        >
          <div className="flex items-center mb-6">
            <FaArrowLeft
              className="text-gray-600 dark:text-gray-400 cursor-pointer mr-3 hover:text-purple dark:hover:text-purple transition-colors"
              onClick={() => navigate(`/steps-list/${id}`, { state })}
              size={20}
            />
            <h1 className="text-2xl font-semibold">{t.addStep}</h1>
          </div>
          <div className="mb-4 relative">
            <label
              htmlFor="name"
              className="block mb-2 text-base font-medium ml-2"
            >
              {t.name}
            </label>
            <div className="relative">
              <input
                type="text"
                id="name"
                className="w-full pl-4 pr-12 py-3 rounded-lg border border-gray-300 shadow-sm focus:outline-none focus:border-purple focus:ring-1 focus:ring-purple dark:bg-darkBackground dark:border-gray-600 dark:focus:border-purple transition-colors cursor-pointer"
                value={stepNames[language] || ""}
                onChange={(e) =>
                  setStepNames({ ...stepNames, [language]: e.target.value })
                }
                onClick={() => setIsStepNameModalOpen(true)}
                autoComplete="off"
              />
              <FaGlobe className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
            </div>
          </div>
          <div className="mb-4 relative">
            <label
              htmlFor="type"
              className="block mb-2 text-base font-medium ml-2"
            >
              {t.type}
            </label>
            <div className="relative">
              <select
                id="type"
                className="w-full pl-4 pr-4 py-3 rounded-lg border border-gray-300 shadow-sm focus:outline-none focus:border-purple focus:ring-1 focus:ring-purple dark:bg-darkBackground dark:border-gray-600 dark:focus:border-purple transition-colors cursor-pointer"
                value={stepType}
                onChange={(e) => setStepType(e.target.value)}
              >
                <option value="" disabled></option>
                {contentTypes.map((contentType) => (
                  <option key={contentType.id} value={contentType.name}>
                    {capitalizeFirstLetter(contentType.name)}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="mb-4">
            <label
              htmlFor="file"
              className="block mb-2 text-base font-medium ml-2"
            >
              {t.file}
            </label>
            <div className="relative">
              <input
                type="file"
                id="file"
                className="hidden"
                onChange={(e) => handleFileChange(e, setStepFile)}
              />
              <label
                htmlFor="file"
                className="w-full p-8 border-2 border-dashed rounded-lg shadow-sm cursor-pointer flex justify-center items-center relative dark:bg-darkBackground hover:border-purple dark:hover:border-purple transition-all"
                style={{ borderColor: stepFile ? "#8B5CF6" : "" }}
              >
                {stepFile ? (
                  <div className="relative w-full flex items-center justify-center">
                    <svg
                      className="w-10 h-10 text-purple mr-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <p className="text-center text-gray-700 dark:text-gray-300 font-medium">
                      {stepFile.name}
                    </p>
                    <button
                      type="button"
                      className="absolute right-0 top-1/2 transform -translate-y-1/2 mr-2 p-2 hover:bg-red-50 dark:hover:bg-red-900 dark:hover:bg-opacity-20 rounded-lg transition-all"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveFile(setStepFile);
                      }}
                    >
                      <FaTimes className="text-red-500 hover:scale-110 transition-transform" />
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <p className="text-center text-gray-600 dark:text-gray-400">
                      {t.uploadFile}
                    </p>
                  </div>
                )}
              </label>
            </div>
          </div>

          {stepType === "object" && (
            <div className="mb-4">
              <label
                htmlFor="secondaryFile"
                className="block mb-2 text-base font-medium ml-2"
              >
                USDZ {t.file}
              </label>
              <div className="relative">
                <input
                  type="file"
                  id="secondaryFile"
                  className="hidden"
                  onChange={(e) => handleFileChange(e, setSecondaryFile)}
                />
                <label
                  htmlFor="secondaryFile"
                  className="w-full p-8 border-2 border-dashed rounded-lg shadow-sm cursor-pointer flex justify-center items-center relative dark:bg-darkBackground hover:border-purple dark:hover:border-purple transition-all"
                  style={{ borderColor: secondaryFile ? "#8B5CF6" : "" }}
                >
                  {secondaryFile ? (
                    <div className="relative w-full flex items-center justify-center">
                      <svg
                        className="w-10 h-10 text-purple mr-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <p className="text-center text-gray-700 dark:text-gray-300 font-medium">
                        {secondaryFile.name}
                      </p>
                      <button
                        type="button"
                        className="absolute right-0 top-1/2 transform -translate-y-1/2 mr-2 p-2 hover:bg-red-50 dark:hover:bg-red-900 dark:hover:bg-opacity-20 rounded-lg transition-all"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveFile(setSecondaryFile);
                        }}
                      >
                        <FaTimes className="text-red-500 hover:scale-110 transition-transform" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <svg
                        className="mx-auto h-12 w-12 text-gray-400 mb-3"
                        stroke="currentColor"
                        fill="none"
                        viewBox="0 0 48 48"
                      >
                        <path
                          d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <p className="text-center text-gray-600 dark:text-gray-400">
                        {t.uploadFile}
                      </p>
                    </div>
                  )}
                </label>
                <p className="text-red-500 font-semibold text-sm mt-2 ml-2">
                  {t2.animation_note}
                </p>
              </div>
            </div>
          )}

          {stepType === "object" && (
            <RotationInput
              rotation={stepRotation}
              setRotation={setStepRotation}
              label={t.rotation}
            />
          )}
          {stepType === "panorama" && (
            <PanoramaInputs
              clickToPaths={stepClickToPaths}
              setClickToPaths={setStepClickToPaths}
              links={links || []}
            />
          )}

          <div className="mb-20 relative">
            <label
              htmlFor="thumbnail"
              className="block mb-2 text-base font-medium ml-2"
            >
              {t.thumbnail}
            </label>
            <div
              className={`w-full p-8 border-2 border-dashed rounded-lg shadow-sm dark:bg-darkBackground transition-all ${
                dragActive
                  ? "border-purple bg-purple bg-opacity-5"
                  : thumbnailUrl || thumbnail
                  ? "border-purple"
                  : "border-gray-300 hover:border-purple dark:hover:border-purple"
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                type="file"
                id="thumbnail"
                className="hidden"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    setThumbnail(e.target.files[0]);
                    setThumbnailUrl(URL.createObjectURL(e.target.files[0]));
                  }
                }}
              />
              <label
                htmlFor="thumbnail"
                className="w-full h-full flex justify-center items-center cursor-pointer relative"
              >
                {thumbnailUrl ? (
                  <div className="relative">
                    <img
                      src={thumbnailUrl}
                      alt="Thumbnail"
                      className="max-h-48 rounded-lg shadow-md"
                    />
                    <button
                      type="button"
                      className="absolute -top-3 -right-3 p-2 bg-white dark:bg-darkSecondaryBackground rounded-full shadow-lg hover:bg-red-50 dark:hover:bg-red-900 dark:hover:bg-opacity-20 transition-all"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleRemoveThumbnail();
                      }}
                    >
                      <FaTimes className="text-red-500 hover:scale-110 transition-transform" />
                    </button>
                  </div>
                ) : thumbnail ? (
                  <div className="relative flex items-center">
                    <svg
                      className="w-10 h-10 text-purple mr-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <p className="text-center text-gray-700 dark:text-gray-300 font-medium">
                      {thumbnail.name}
                    </p>
                    <button
                      type="button"
                      className="absolute -right-12 p-2 hover:bg-red-50 dark:hover:bg-red-900 dark:hover:bg-opacity-20 rounded-lg transition-all"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleRemoveThumbnail();
                      }}
                    >
                      <FaTimes className="text-red-500 hover:scale-110 transition-transform" />
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400 mb-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <p className="text-center text-gray-600 dark:text-gray-400 font-medium">
                      {t.thumbnailDrop}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                      or click to browse
                    </p>
                  </div>
                )}
              </label>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-6">
            <button
              type="button"
              className="bg-gray-300 text-gray-700 px-6 py-2.5 rounded-lg font-medium shadow-sm hover:bg-opacity-90 transition-all dark:bg-white dark:text-black"
              onClick={() => navigate(-1)}
            >
              {t.discard}
            </button>
            <button
              type="submit"
              className="bg-purple text-white px-6 py-2.5 rounded-lg font-medium shadow-sm hover:bg-opacity-90 transition-all"
              form="add-steps-form"
            >
              {t.save}
            </button>
          </div>
        </form>
      </div>
      <AddLanguageModal
        isOpen={isStepNameModalOpen}
        onClose={() => setIsStepNameModalOpen(false)}
        translations={t}
        mode="stepName"
        values={stepNames}
        setValues={setStepNames}
      />
    </div>
  );
};

export default CreateStep;
